import React, { useEffect, useState, useRef } from "react";
import * as routes from "../../../app/routes";
import { HeaderField, InfiniteScrollList } from "../../../components/table";
import { useSelector } from "react-redux";
import {
  userClientDb,
  uiLanguage,
  userDepartmentId,
} from "../../auth/selectors/userSelector";
import { getDocHeaderById } from "../../api/docListsandDetails";
import {
  canUserModifyQuantities,
  getHeaderAdjustmentParams,
} from "../../api/parameters";
import {
  completeTheOperation,
  checkDetails,
  runClientsProcedure,
} from "../../api/completion";
import { Alert } from "../../../components";
import { useTranslation } from "react-i18next";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import { useCompletionStyles } from "../css";
import {
  getErrorMessage,
  getWarningMessage,
} from "../../../components/form/formUtils";
import { useAuthorizationError, useLoader } from "../../../hooks";
import * as modules from "../../../utils/modules";
import { moduleType } from "../../../utils/modules";
import { Autocomplete, Button, InputAdornment, TextField } from "@mui/material";
import { ErrorOutlineOutlined, EditOutlined } from "@mui/icons-material";

function CompletionDetails() {
  const parameters = useParams<{ id: string; module: moduleType }>();
  const id = parameters.id || "";
  const module = parameters.module || "";
  const [logoutUser] = useAuthorizationError();
  const { classes } = useCompletionStyles();
  const [redirect, setRedirect] = useState(false);
  const [showOk, setShowOk] = useState(false);
  const [note, setNote] = useState("");
  const [showWarning, setShowWarning] = useState(false);
  const [noteList, setNoteList] =
    useState<Array<{ value: number; label: string }>>();
  const [showAlert, setShowAlert] = useState<{
    show: boolean;
    message: string;
    status: number;
    iconColor: "success" | "error" | "warning";
    closeAfterError?: boolean;
  }>({ show: false, message: "", status: 0, iconColor: "warning" });
  const userDb = useSelector(userClientDb);
  const locale = useSelector(uiLanguage);
  const departmentId = useSelector(userDepartmentId);
  const userDbId = userDb ? userDb.id : 0;
  const [translate] = useTranslation();
  const [data, setData] = useState([]);
  const [, setLoader] = useLoader();
  const [headerData, setHeaderData] = useState({
    operationCode: "",
    operationType: "",
  });
  const navigate = useNavigate();

  const finalizeOption = useRef<0 | 1 | 2 | undefined>(undefined);

  const [canUserCompleteWithErr, setCanUserCompleteWithErr] = useState(false);
  const [canUserModify, setCanUserModify] = useState(false);

  const headerParams = {
    clientDbId: userDbId,
    operationCode: id,
    module: module,
    locale: locale,
  };

  const loadDataparams = React.useMemo<{
    cellType: "header" | "info" | "item";
  }>(
    () => ({
      cellType: "item",
      complete: true,
    }),
    [],
  );

  const link = React.useMemo(() => {
    let linkTo = { details: "/", list: "/" };

    switch (module) {
      case modules.MODULE_SALES:
        linkTo = {
          details: routes.ROUTE_SALES_DETAILS.path + `/${id}`,
          list: routes.ROUTE_SALES.path,
        };
        break;
      case modules.MODULE_PURCHASES:
        linkTo = {
          details: routes.ROUTE_PURCHASES_DETAILS.path + `/${id}`,
          list: routes.ROUTE_PURCHASES.path,
        };
        break;
      case modules.MODULE_INTERNAL:
        linkTo = {
          details: routes.ROUTE_INTERNAL_DETAILS.path + `/${id}`,
          list: routes.ROUTE_INTERNAL.path,
        };
        break;

      case modules.MODULE_SCANNER:
        linkTo = {
          details: routes.ROUTE_SCANNER_DETAILS.path + `/${id}`,
          list: routes.ROUTE_SCANNER.path,
        };
        break;

      case modules.MODULE_INVENTORY:
        linkTo = {
          details: routes.ROUTE_INVENTORY_DETAILS.path + `/${id}`,
          list: routes.ROUTE_INVENTORY.path,
        };
        break;

      case modules.MODULE_ASSEMBLY:
        linkTo = {
          details: routes.ROUTE_ASSEMBLY_DETAILS.path + `/${id}`,
          list: routes.ROUTE_ASSEMBLY.path,
        };
        break;

      case modules.MODULE_LOCATION:
        linkTo = {
          details: routes.ROUTE_LOCATION_DETAILS.path + `/${id}`,
          list: routes.ROUTE_LOCATION.path,
        };
        break;
    }

    return linkTo;
  }, [module, id]);

  const check = React.useCallback(
    (completionParams: any) => {
      checkDetails(completionParams)
        .then((result) => {
          setLoader(false);
          if (result.warnings.length > 0) {
            setData(result.warnings);
            setShowWarning(true);
          } else {
            setShowOk(true);
          }
        })
        .catch((reason) => {
          setLoader(false);
          const message = getErrorMessage(reason);
          setShowAlert({
            show: true,
            message: message.message,
            status: message.status,
            iconColor: "error",
          });
        });
    }, // eslint-disable-next-line
    [],
  );

  const finalize = React.useCallback(
    async (completionParams: any) => {
      setLoader(true);

      let isClientProcedure = false;
      let customProcedure: any = 0;
      let customWarnings: any[] = [];

      try {
        const { data, warnings } = await completeTheOperation(completionParams);

        if (warnings.length > 0 && warnings[0].customProcedure) {
          customProcedure = warnings[0].customProcedure;
        } else if (data.length > 0 && data[0].customProcedure) {
          customProcedure = data[0].customProcedure;
        }

        if (
          (typeof customProcedure === "string" && customProcedure === "1") ||
          customProcedure === 1
        )
          isClientProcedure = true;

        try {
          if (isClientProcedure) {
            const { warnings } = await runClientsProcedure(completionParams);
            if (warnings.length > 0) customWarnings = [...warnings];
          }
        } catch (reason) {
          setLoader(false);
          const message = getErrorMessage(reason);
          setShowAlert({
            show: true,
            message: message.message,
            status: message.status,
            iconColor: "error",
            closeAfterError: true,
          });
          return;
        }

        setLoader(false);

        if (customWarnings && customWarnings.length > 0) {
          const warningMessage = getWarningMessage(customWarnings);

          setShowAlert({
            show: true,
            message: warningMessage,
            status: 0,
            iconColor: "warning",
          });
        } else if (warnings && warnings.length > 0) {
          const warningMessage = getWarningMessage(warnings);

          setShowAlert({
            show: true,
            message: warningMessage,
            status: 0,
            iconColor: "warning",
          });
        } else {
          const message = translate("success", "Operacija baigta sėkmingai!");

          setShowAlert({
            show: true,
            message: message,
            status: 0,
            iconColor: "success",
          });
        }
      } catch (reason) {
        setLoader(false);
        const message = getErrorMessage(reason);
        setShowAlert({
          show: true,
          message: message.message,
          status: message.status,
          iconColor: "error",
        });
      }
    }, // eslint-disable-next-line
    [translate],
  );

  useEffect(
    () => {
      setLoader(true);

      getDocHeaderById(headerParams)
        .then(({ data }) => {
          if (data.length === 0) {
            navigate(routes.ROUTE_ERROR.path);
          }
          const header = data[0];
          setHeaderData(header);
          if (data[0] && data[0].note) setNote(data[0].note);
        })
        .then(() => {
          const completionParams = {
            ...headerParams,
            operationType: headerData.operationType,
          };

          check(completionParams);
        })
        .catch((reason) => {
          setLoader(false);
          const message = getErrorMessage(reason);
          setShowAlert({
            show: true,
            message: message.message,
            status: message.status,
            iconColor: "error",
          });
        });
    }, // eslint-disable-next-line
    [],
  );

  useEffect(() => {
    const params = {
      clientDbId: userDbId,
      locale: locale,
      module: module,
      operationType: headerData.operationType,
      operationCode: headerData.operationCode,
    };

    if (headerData.operationType !== "") {
      canUserModifyQuantities(params)
        .then(({ data }) => {
          if (
            data.length > 0 &&
            data[0].canFinalizeWithQuantitiesModification &&
            (data[0].canFinalizeWithQuantitiesModification === 1 ||
              data[0].canFinalizeWithQuantitiesModification === "1")
          )
            setCanUserModify(true);

          if (
            data.length > 0 &&
            data[0].canFinalizeWithoutQuantitiesModification &&
            (data[0].canFinalizeWithoutQuantitiesModification === 1 ||
              data[0].canFinalizeWithoutQuantitiesModification === "1")
          )
            setCanUserCompleteWithErr(true);
        })
        .catch((reason) => {
          const message = getErrorMessage(reason);
          setShowAlert({
            show: true,
            message: message.message,
            status: message.status,
            iconColor: "error",
          });
        });
    }
  }, [headerData.operationType, headerData.operationCode]);

  React.useEffect(
    () => {
      const params = {
        clientDbId: userDbId,
        locale: locale,
        module: module,
        operationType: headerData.operationType,
      };

      getHeaderAdjustmentParams(params)
        .then(({ data }) => {
          if (data && data[0] && data[0].value1) {
            const options = data[0].value1;
            const descArray = options.split(/,\s*/).map((option: any) => {
              return { label: option, value: option };
            });
            setNoteList(descArray);
          }
        })
        .catch((reason) => {
          const message = getErrorMessage(reason);
          setShowAlert({
            show: true,
            message: message.message,
            status: message.status,
            iconColor: "error",
          });
        });
    }, // eslint-disable-next-line
    [],
  );

  const handleCompletion = (
    option?: 0 | 1 | 2,
    warningId?: string,
    ignoreWarnings?: number,
  ) => {
    //Issaugome reikalinga finalizeOption reiksme, kad galetume ja panaudoti finalize funkcijoje
    if (option) finalizeOption.current = option;

    const completionParams: any = {
      ...headerParams,
      operationType: headerData.operationType,
      locale: locale,
      note: note,
      departmentId: departmentId,
      warningId: warningId || undefined,
      finalizeOption: finalizeOption.current,
    };

    if (ignoreWarnings !== undefined) {
      completionParams.ignoreWarnings = ignoreWarnings;
    }

    finalize(completionParams);
  };

  const handleContinue = () => {
    const warningId = localStorage.getItem("warningId");
    handleCompletion(undefined, warningId || undefined, 1);
    localStorage.removeItem("warningId");
  };

  const handleConfirmDisassemble = () => {
    const warningId = localStorage.getItem("warningId");
    handleCompletion(undefined, warningId || undefined, 1);
    localStorage.removeItem("warningId");
    localStorage.removeItem("btn1");
  };

  const handleConfirmDisassembleAndUpload = () => {
    const warningId = localStorage.getItem("warningId");
    handleCompletion(undefined, warningId || undefined, 2);
    localStorage.removeItem("warningId");
    localStorage.removeItem("btn2");
  };

  const handleConfirmUploadAll = () => {
    const warningId = localStorage.getItem("warningId");
    handleCompletion(undefined, warningId || undefined, 3);
    localStorage.removeItem("warningId");
    localStorage.removeItem("btn3");
  };

  const handleErrorClose = () => {
    logoutUser(showAlert.status);
    if (showAlert.status !== 401) {
      showAlert.closeAfterError
        ? setRedirect(true)
        : setShowAlert({
            show: false,
            message: "",
            status: 0,
            iconColor: "error",
          });
    }
  };

  const handleCompletionClose = () => {
    const btn1 = localStorage.getItem("btn1");
    const btn2 = localStorage.getItem("btn2");
    const btn3 = localStorage.getItem("btn3");
    const warningId = localStorage.getItem("warningId");

    const hasCustomButtons = !!btn1 || !!btn2 || !!btn3;
    const hasWarningId = !!warningId;

    const isWarning = showAlert.iconColor === "warning";
    const isSuccess = showAlert.iconColor === "success";

    if (isSuccess || (isWarning && !hasCustomButtons && !hasWarningId)) {
      setRedirect(true);
    } else {
      setShowAlert({
        show: false,
        message: "",
        status: 0,
        iconColor: "warning",
      });
    }
  };

  const handleNoteAuto = (event: React.SyntheticEvent, value: any) => {
    setNote(value && value.value ? value.value : "");
  };

  const handleNote = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNote(event.target.value);
  };

  const showError = (
    <Alert
      isOpen={showAlert.show}
      message={showAlert.message}
      onClose={
        showAlert.iconColor === "error"
          ? handleErrorClose
          : handleCompletionClose
      }
      iconColor={showAlert.iconColor}
      isConfirmationButton={showAlert.iconColor !== "error"}
      onConfirm={handleContinue}
      onConfirmDisassemble={handleConfirmDisassemble}
      onConfirmDisassembleAndUpload={handleConfirmDisassembleAndUpload}
      onConfirmUploadAll={handleConfirmUploadAll}
    />
  );

  return (
    <div className={classes.mainContainer}>
      {showAlert.show && showError}

      {redirect && <Navigate to={link.list} />}

      {showWarning && (
        <div className={classes.warning}>
          <ErrorOutlineOutlined fontSize="small" />
          {translate("foundErrors")}
        </div>
      )}

      <div className={classes.header}>
        <HeaderField
          data={headerData}
          link={showOk ? link.list : link.details}
          headerColor="grey"
          module={module}
        />
      </div>

      {showWarning && (
        <div className={classes.mainContainer}>
          <InfiniteScrollList
            initialData={data}
            loadDataparams={loadDataparams}
          />
        </div>
      )}

      <div>
        <div className={classes.note}>
          {noteList && noteList.length > 0 ? (
            <Autocomplete
              fullWidth
              options={noteList}
              getOptionLabel={(option: any) => option.value}
              size="small"
              onChange={handleNoteAuto}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label={translate("note", "Pastaba")}
                  value={note}
                  autoFocus
                />
              )}
            />
          ) : (
            <TextField
              fullWidth
              label={translate("note", "Pastaba")}
              onChange={handleNote}
              multiline
              value={note}
              size="small"
              autoFocus
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <EditOutlined />
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        </div>

        {showOk && (
          <div className={classes.actionButtonsContainer}>
            <Button
              fullWidth={true}
              onClick={() => handleCompletion(0)}
              className={classes.actionButtons}
            >
              {translate("completed", "Baigti")}
            </Button>
          </div>
        )}

        {showWarning && (
          <div className={classes.actionButtonsContainer}>
            {canUserCompleteWithErr && (
              <div className={classes.container}>
                <Button
                  fullWidth={true}
                  onClick={() => handleCompletion(1)}
                  className={classes.actionButtons}
                >
                  {translate("completeWithErrors", "Baigti su netikslumais")}
                </Button>
              </div>
            )}

            {canUserModify && (
              <div className={classes.container}>
                <Button
                  fullWidth={true}
                  onClick={() => handleCompletion(2)}
                  className={classes.actionButtons}
                >
                  {translate("completeWithAdjustments", "Baigti koreguojant")}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CompletionDetails;
